import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const AdminPanel = () => {
    const { user } = useContext(AuthContext);
    const { showToast } = useToast();
    const [activeTab, setActiveTab] = useState('import');

    // Redirect if not staff
    if (!user || !user.is_staff) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-plantlix-50 to-plantlix-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-plantlix-xl p-8 max-w-md text-center">
                    <div className="text-6xl mb-4">🔒</div>
                    <h1 className="text-2xl font-bold text-gray-800 mb-2">Zugriff verweigert</h1>
                    <p className="text-gray-600">
                        Diese Seite ist nur für Administratoren zugänglich.
                    </p>
                </div>
            </div>
        );
    }

    const tabs = [
        { id: 'import', icon: '🌿', label: 'Pflanzen Importieren' },
        { id: 'images', icon: '🖼️', label: 'Bilder Hochladen' },
        { id: 'export', icon: '📊', label: 'Daten Export' },
        { id: 'api', icon: '🔄', label: 'Daten-Anreicherung' },
        { id: 'jobs', icon: '💼', label: 'Arbeitgeber' },
        { id: 'history', icon: '📜', label: 'Upload-Historie' },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-plantlix-50 via-plantlix-100 to-mint-100 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-2xl shadow-plantlix-lg p-6 mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800 mb-2">
                                🛠️ Admin Panel
                            </h1>
                            <p className="text-gray-600">
                                Datensammlung & Verwaltung
                            </p>
                        </div>
                        <div className="text-sm text-gray-500">
                            Angemeldet als: <span className="font-medium text-plantlix-600">{user.username}</span>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-2xl shadow-plantlix-lg overflow-hidden">
                    <div className="flex border-b border-plantlix-100 bg-plantlix-50/50 overflow-x-auto">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-6 py-4 font-medium transition-all whitespace-nowrap ${activeTab === tab.id
                                    ? 'bg-white text-plantlix-600 border-b-2 border-plantlix-500'
                                    : 'text-gray-600 hover:bg-white/50 hover:text-plantlix-600'
                                    }`}
                            >
                                <span className="text-xl">{tab.icon}</span>
                                <span>{tab.label}</span>
                            </button>
                        ))}
                    </div>

                    {/* Tab Content */}
                    <div className="p-6">
                        {activeTab === 'import' && <PlantImportPanel />}
                        {activeTab === 'images' && <ImageUploadPanel />}
                        {activeTab === 'export' && <DataExportPanel />}
                        {activeTab === 'api' && <ScrapingConsolePanel />}
                        {activeTab === 'jobs' && <EmployerManagementPanel />}
                        {activeTab === 'history' && <UploadHistoryPanel />}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Panel Components
const PlantImportPanel = () => {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const { showToast } = useToast();

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            if (selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.csv')) {
                setFile(selectedFile);
            } else {
                showToast('Nur Excel (.xlsx) und CSV (.csv) Dateien erlaubt', 'error');
            }
        }
    };

    const handleImport = async () => {
        if (!file) {
            showToast('Bitte wähle eine Datei aus', 'error');
            return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('format', file.name.endsWith('.xlsx') ? 'excel' : 'csv');

        try {
            const response = await fetch('/api/admin/api/backup/import/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: formData
            });

            const data = await response.json();

            if (response.ok) {
                showToast(`✅ ${data.imported_records || 0} Pflanzen importiert!`, 'success');
                setFile(null);
            } else {
                throw new Error(data.error || 'Import fehlgeschlagen');
            }
        } catch (err) {
            showToast(`Fehler: ${err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-plantlix-50/50 rounded-xl p-6 border-2 border-plantlix-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    📥 Excel/CSV Datei hochladen
                </h3>

                <div className="space-y-4">
                    <div className="border-2 border-dashed border-plantlix-300 rounded-xl p-8 text-center hover:border-plantlix-500 transition-colors">
                        <input
                            type="file"
                            accept=".xlsx,.csv"
                            onChange={handleFileChange}
                            className="hidden"
                            id="file-upload"
                        />
                        <label htmlFor="file-upload" className="cursor-pointer">
                            <div className="text-4xl mb-2">📄</div>
                            <p className="text-gray-700 font-medium mb-1">
                                {file ? file.name : 'Klicke um Datei auszuwählen'}
                            </p>
                            <p className="text-sm text-gray-500">
                                Excel (.xlsx) oder CSV (.csv), max. 50MB
                            </p>
                        </label>
                    </div>

                    <button
                        onClick={handleImport}
                        disabled={!file || loading}
                        className="w-full bg-gradient-to-r from-plantlix-500 to-plantlix-600 hover:from-plantlix-600 hover:to-plantlix-700 text-white font-bold py-4 rounded-xl shadow-plantlix-md hover:shadow-plantlix-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? '⏳ Importiere...' : '✅ Importieren'}
                    </button>
                </div>
            </div>

            <div className="bg-plantlix-50/50 rounded-xl p-4 border border-plantlix-200">
                <h4 className="font-medium text-plantlix-900 mb-2">💡 Spalten-Format:</h4>
                <p className="text-sm text-plantlix-800">
                    <span className="font-mono bg-plantlix-100/50 px-2 py-1 rounded">botanical_name</span>,{' '}
                    <span className="font-mono bg-plantlix-100/50 px-2 py-1 rounded">german_name</span>,{' '}
                    <span className="font-mono bg-plantlix-100/50 px-2 py-1 rounded">family</span>,{' '}
                    <span className="font-mono bg-plantlix-100/50 px-2 py-1 rounded">category</span>
                </p>
            </div>
        </div>
    );
};

const ImageUploadPanel = () => {
    return (
        <div className="text-center text-gray-500 py-12">
            🖼️ Bild-Upload-Feature kommt bald...
        </div>
    );
};

const DataExportPanel = () => {
    const [format, setFormat] = useState('excel');
    const [loading, setLoading] = useState(false);
    const { showToast } = useToast();

    const handleExport = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/admin/api/backup/export/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({ format, include_sensitive: false })
            });

            const data = await response.json();

            if (response.ok && data.download_url) {
                window.location.href = data.download_url;
                showToast('✅ Export erfolgreich!', 'success');
            } else {
                throw new Error(data.error || 'Export fehlgeschlagen');
            }
        } catch (err) {
            showToast(`Fehler: ${err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-plantlix-50/50 rounded-xl p-6 border-2 border-plantlix-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    📊 Pflanzen exportieren
                </h3>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Format wählen:
                        </label>
                        <select
                            value={format}
                            onChange={(e) => setFormat(e.target.value)}
                            className="w-full px-4 py-3 rounded-xl border-2 border-plantlix-200 focus:border-plantlix-500 focus:outline-none"
                        >
                            <option value="excel">Excel (.xlsx)</option>
                            <option value="csv">CSV (.csv)</option>
                            <option value="json">JSON (.json)</option>
                            <option value="sqlite">SQLite Datenbank</option>
                        </select>
                    </div>

                    <button
                        onClick={handleExport}
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-plantlix-500 to-plantlix-600 hover:from-plantlix-600 hover:to-plantlix-700 text-white font-bold py-4 rounded-xl shadow-md hover:shadow-lg transition-all disabled:opacity-50"
                    >
                        {loading ? '⏳ Exportiere...' : '📥 Jetzt exportieren'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ScrapingConsolePanel = () => {
    const { token } = useContext(AuthContext);
    const { showToast } = useToast();
    // basic states
    const [status, setStatus] = useState('idle'); // idle, running, stopping
    const [taskId, setTaskId] = useState(null);
    const [progress, setProgress] = useState(null);
    const [logs, setLogs] = useState([]);

    useEffect(() => {
        let interval;
        if (status === 'running' && taskId) {
            interval = setInterval(checkProgress, 2000); // Poll every 2s
        }
        return () => clearInterval(interval);
    }, [status, taskId]);

    const checkProgress = async () => {
        if (!token) return;
        try {
            const res = await fetch(`/api/admin/scraping/progress/${taskId}/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();

            if (data.success && data.progress) {
                setProgress(data.progress);

                // Update Logs (append only new ones in real implementation, simplified here)
                if (data.progress.current_log) {
                    setLogs(prev => {
                        const last = prev[prev.length - 1];
                        if (last !== data.progress.current_log) {
                            return [...prev, data.progress.current_log].slice(-10); // keep last 10
                        }
                        return prev;
                    });
                }

                if (data.progress.status === 'completed' || data.progress.status === 'failed') {
                    setStatus('idle');
                    showToast(`Scraping ${data.progress.status === 'completed' ? 'abgeschlossen' : 'abgebrochen'}`, data.progress.status === 'completed' ? 'success' : 'error');
                }
            }
        } catch (error) {
            console.error("Progress fetch error", error);
        }
    };

    const handleStart = async () => {
        if (!token) {
            showToast('Authentifizierungs-Token fehlt', 'error');
            return;
        }
        try {
            setStatus('running');
            setLogs(['Starte Anreicherung...']);
            const res = await fetch('/api/admin/scraping/start/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ sources: ['trefle', 'wiki', 'gbif'] }) // Default sources
            });
            const data = await res.json();
            if (data.task_id) {
                setTaskId(data.task_id);
                showToast('Anreichung gestartet', 'success');
            } else {
                setStatus('idle');
                showToast(data.error || 'Fehler beim Starten', 'error');
            }
        } catch (error) {
            setStatus('idle');
            showToast('Verbindungsfehler', 'error');
        }
    };

    const handleStop = async () => {
        if (!taskId) return;
        try {
            setStatus('stopping');
            await fetch(`/api/admin/scraping/stop/${taskId}/`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            showToast('Stopp angefordert...', 'info');
        } catch (error) {
            showToast('Fehler beim Stoppen', 'error');
        }
    };

    return (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-plantlix-100">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h3 className="text-lg font-bold text-gray-800">🌱 Automatische Daten-Anreicherung</h3>
                    <p className="text-gray-500 text-sm">Sammelt Daten von Trefle, Wikipedia und GBIF</p>
                </div>
                <div>
                    {status === 'running' ? (
                        <button onClick={handleStop} className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors font-medium animate-pulse">
                            ⏹️ Stoppen
                        </button>
                    ) : (
                        <button onClick={handleStart} className="px-4 py-2 bg-plantlix-600 text-white rounded-lg hover:bg-plantlix-700 transition-colors font-medium shadow-plantlix-md">
                            ▶️ Starten
                        </button>
                    )}
                </div>
            </div>

            {/* Progress Bar */}
            {status !== 'idle' && progress && (
                <div className="mb-8">
                    <div className="flex justify-between text-sm mb-1 font-medium text-gray-700">
                        <span>{progress.current_operation || 'Initialisiere...'}</span>
                        <span>{progress.progress || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                        <div
                            className="bg-gradient-to-r from-plantlix-400 to-plantlix-600 h-3 rounded-full transition-all duration-500"
                            style={{ width: `${progress.progress || 0}%` }}
                        />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-2">
                        <span>Gesamt: {progress.total_plants || 0}</span>
                        <span className="text-plantlix-600">Erfolgreich: {progress.stats?.updated || 0}</span>
                        <span className="text-red-500">Fehler: {progress.stats?.errors || 0}</span>
                    </div>
                </div>
            )}

            {/* Live Logs */}
            <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm h-64 overflow-y-auto">
                {logs.length === 0 && <span className="text-gray-500 opacity-50">Bereit zum Starten...</span>}
                {logs.map((log, i) => (
                    <div key={i} className="text-plantlix-400 mb-1 border-b border-gray-800 pb-1 last:border-0">
                        <span className="opacity-50 mr-2">[{new Date().toLocaleTimeString()}]</span>
                        {log}
                    </div>
                ))}
            </div>
        </div>
    );
};

const EmployerManagementPanel = () => {
    const { fetchWithAuth } = useContext(AuthContext);
    const { showToast } = useToast();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const res = await fetchWithAuth('/api/admin/employer-requests/pending/');
            if (res.ok) setRequests(await res.json());
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
    }, []);

    const handleAction = async (userId, action) => {
        try {
            const res = await fetchWithAuth(`/api/admin/employer-requests/${userId}/respond/`, {
                method: 'POST',
                body: JSON.stringify({ action })
            });
            if (res.ok) {
                showToast(`Anfrage ${action === 'approve' ? 'genehmigt' : 'abgelehnt'}`, 'success');
                fetchRequests();
            } else {
                showToast('Aktion fehlgeschlagen', 'error');
            }
        } catch (error) {
            showToast('Netzwerkfehler', 'error');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-800">💼 Arbeitgeber-Anfragen</h3>
                <button onClick={fetchRequests} className="text-sm text-plantlix-600 font-bold hover:underline">Aktualisieren</button>
            </div>

            {loading ? (
                <div className="animate-pulse space-y-4">
                    {[1, 2].map(i => <div key={i} className="h-24 bg-gray-100 rounded-xl" />)}
                </div>
            ) : requests.length > 0 ? (
                <div className="grid gap-4">
                    {requests.map(req => (
                        <div key={req.user_id} className="bg-white border-2 border-plantlix-100 rounded-2xl p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 transition-all hover:border-plantlix-300">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="font-black text-lg text-gray-900">{req.username}</span>
                                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-[10px] font-black uppercase tracking-wider">Ausstehend</span>
                                </div>
                                <p className="text-sm text-gray-500 mb-1">{req.email}</p>
                                <p className="text-xs text-gray-400">Beantragt am: {new Date(req.requested_at).toLocaleString()}</p>
                            </div>
                            <div className="flex gap-2 w-full md:w-auto">
                                <button 
                                    onClick={() => handleAction(req.user_id, 'reject')}
                                    className="flex-1 md:px-6 py-3 bg-red-50 text-red-600 border border-red-100 rounded-xl text-sm font-bold hover:bg-red-100 transition-colors"
                                >
                                    Ablehnen
                                </button>
                                <button 
                                    onClick={() => handleAction(req.user_id, 'approve')}
                                    className="flex-1 md:px-6 py-3 bg-plantlix-600 text-white rounded-xl text-sm font-black shadow-lg shadow-plantlix-600/20 hover:bg-plantlix-700 transition-colors"
                                >
                                    Freischalten
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-20 bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200">
                    <div className="text-4xl mb-4">✨</div>
                    <p className="text-gray-500 font-bold">Keine offenen Anfragen vorhanden.</p>
                </div>
            )}
        </div>
    );
};

export default AdminPanel;
